import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

interface CategorySuggestion {
  category: string;
  confidence: number;
  reason: string;
}

export function useCategorySuggestion(familyId: string | null) {
  const [suggestion, setSuggestion] = useState<CategorySuggestion | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastTitleRef = useRef("");

  function suggest(title: string) {
    const trimmed = title.trim();

    // Clear previous debounce
    if (debounceRef.current) clearTimeout(debounceRef.current);

    // Reset if empty or too short
    if (trimmed.length < 2) {
      setSuggestion(null);
      lastTitleRef.current = "";
      return;
    }

    // Don't re-query same title
    if (trimmed === lastTitleRef.current) return;

    debounceRef.current = setTimeout(async () => {
      lastTitleRef.current = trimmed;
      setIsLoading(true);
      try {
        const { data, error } = await supabase.functions.invoke("suggest-category", {
          body: { title: trimmed, familyId, actorType: "parent" },
        });
        if (error) throw error;
        setSuggestion(data as CategorySuggestion);
      } catch (err) {
        console.warn("Category suggestion failed:", err);
        setSuggestion(null);
      } finally {
        setIsLoading(false);
      }
    }, 700);
  }

  function reset() {
    setSuggestion(null);
    lastTitleRef.current = "";
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return { suggestion, isLoading, suggest, reset };
}
